/**
 * JSON document editor used by blueprint and Industry Pack seed panels.
 *
 * The foundation deliberately edits opaque documents as raw JSON. Parsed values
 * are lifted to the host only when valid, so a half-typed document never
 * overwrites saved state.
 *
 * Text is local while the user types. External `value` resets are applied only when
 * `documentKey` changes (load / save / publish), so pretty-printing never steals the
 * caret mid-keystroke.
 *
 * `rootKind` selects object vs array roots — Industry Pack `enabled_plugins` is an
 * array; agent blueprint panels are objects.
 */

import React, { useEffect, useId, useRef, useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';

export type JsonRootKind = 'object' | 'array';

type JsonRoot = Record<string, unknown> | unknown[];

interface JsonPanelProps {
  label: string;
  hint?: string;
  value: JsonRoot;
  /**
   * Changes only when the host intentionally replaces the document (initial load,
   * successful save, publish). Unrelated parent re-renders must not share a key.
   */
  documentKey: string;
  /** Defaults to object — use `array` for lists such as enabled_plugins. */
  rootKind?: JsonRootKind;
  disabled?: boolean;
  rows?: number;
  onChange: (value: JsonRoot) => void;
  /** Reported so the host can block Save while any panel holds invalid JSON. */
  onValidityChange?: (valid: boolean) => void;
}

function pretty(value: JsonRoot): string {
  return JSON.stringify(value, null, 2);
}

function parseRoot(text: string, rootKind: JsonRootKind): JsonRoot | null {
  try {
    const parsed = JSON.parse(text) as unknown;
    if (rootKind === 'array') {
      return Array.isArray(parsed) ? parsed : null;
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

export const JsonPanel: React.FC<JsonPanelProps> = ({
  label,
  hint,
  value,
  documentKey,
  rootKind = 'object',
  disabled = false,
  rows = 8,
  onChange,
  onValidityChange,
}) => {
  const { t } = useLanguage();
  const generatedId = useId();
  const fieldId = `json-panel-${generatedId}`;
  const [text, setText] = useState(() => pretty(value));
  const [invalid, setInvalid] = useState(false);
  const lastKeyRef = useRef(documentKey);

  // Accept an external document only when the host signals a new revision.
  useEffect(() => {
    if (lastKeyRef.current === documentKey) return;
    lastKeyRef.current = documentKey;
    setText(pretty(value));
    setInvalid(false);
    onValidityChange?.(true);
  }, [documentKey, onValidityChange, value]);

  // On mount (and after remount), sync validity with the host so a previously
  // invalid field cannot leave Save disabled after the editor was remounted clean.
  useEffect(() => {
    onValidityChange?.(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only handshake
  }, []);

  const handleChange = (nextText: string) => {
    setText(nextText);
    const parsed = parseRoot(nextText, rootKind);
    if (!parsed) {
      setInvalid(true);
      onValidityChange?.(false);
      return;
    }
    setInvalid(false);
    onValidityChange?.(true);
    onChange(parsed);
  };

  return (
    <div>
      <div className="mb-1 flex items-center gap-1.5">
        <label
          htmlFor={fieldId}
          className="text-base font-medium text-[var(--color-surface-text)]"
        >
          {label}
        </label>
        {hint ? (
          <span
            className="inline-flex shrink-0 text-[var(--color-surface-text-secondary)]"
            title={hint}
            aria-label={hint}
          >
            <InformationCircleIcon className="h-4 w-4" aria-hidden />
          </span>
        ) : null}
      </div>
      <textarea
        id={fieldId}
        rows={rows}
        value={text}
        disabled={disabled}
        spellCheck={false}
        onChange={(event) => handleChange(event.target.value)}
        className={`w-full rounded border bg-[var(--color-surface-bg)] px-3 py-2 font-mono text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-60 ${
          invalid ? 'border-[var(--color-error)]' : 'border-[var(--color-border)]'
        }`}
      />
      {invalid && (
        <p role="alert" className="mt-1 text-base text-[var(--color-error)]">
          {t('aiStudio.common.invalidJson')}
        </p>
      )}
    </div>
  );
};
